import { useEffect, useState } from "react";
import { useAdmin } from "../contexts/AdminContext";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { ErrorBanner } from "../components/ErrorBanner";
import { indexErrorMessage, formatDate } from "../lib/utils";
import type { FreeRegistrationCode, FreeSubscriptionCode } from "../lib/types";

function expiresAt(code: FreeRegistrationCode): bigint {
  return code.created + code.expiresIn;
}

export function FreeCodesPage() {
  const { actor, isInstaller } = useAdmin();

  // ── registration codes ─────────────────────────────────────────────
  const [regCodes, setRegCodes] = useState<FreeRegistrationCode[]>([]);
  const [regLoading, setRegLoading] = useState(true);
  const [regError, setRegError] = useState("");
  const [regDeleteTarget, setRegDeleteTarget] = useState<FreeRegistrationCode | null>(null);
  const [regDeleting, setRegDeleting] = useState(false);
  const [regCode, setRegCode] = useState("");
  const [regDays, setRegDays] = useState("");
  const [regAdding, setRegAdding] = useState(false);
  const [regAddError, setRegAddError] = useState("");

  // ── subscription codes ─────────────────────────────────────────────
  const [subCodes, setSubCodes] = useState<FreeSubscriptionCode[]>([]);
  const [subLoading, setSubLoading] = useState(true);
  const [subError, setSubError] = useState("");
  const [subDeleteTarget, setSubDeleteTarget] = useState<FreeSubscriptionCode | null>(null);
  const [subDeleting, setSubDeleting] = useState(false);
  const [subCode, setSubCode] = useState("");
  const [subValidDays, setSubValidDays] = useState("");
  const [subDurationDays, setSubDurationDays] = useState("");
  const [subAdding, setSubAdding] = useState(false);
  const [subAddError, setSubAddError] = useState("");

  async function loadRegCodes() {
    if (!actor) return;
    try {
      const res = await actor.listFreeRegistrationCodes();
      if ("ok" in res) setRegCodes(res.ok);
      else setRegError("Failed to load registration codes");
    } catch (e) {
      setRegError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setRegLoading(false);
    }
  }

  async function loadSubCodes() {
    if (!actor) return;
    try {
      const res = await actor.listFreeSubscriptionCodes();
      if ("ok" in res) setSubCodes(res.ok);
      else setSubError("Failed to load subscription codes");
    } catch (e) {
      setSubError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setSubLoading(false);
    }
  }

  useEffect(() => {
    loadRegCodes();
    loadSubCodes();
  }, [actor]); // eslint-disable-line react-hooks/exhaustive-deps

  async function addRegCode() {
    if (!actor || !regCode.trim() || !regDays.trim()) return;
    const days = parseInt(regDays, 10);
    if (isNaN(days) || days <= 0) { setRegAddError("Valid days must be a positive number"); return; }
    setRegAdding(true);
    setRegAddError("");
    try {
      const res = await actor.createFreeRegistrationCode({ code: regCode.trim().toUpperCase(), validDays: BigInt(days) });
      if ("err" in res) {
        setRegAddError(indexErrorMessage(res.err));
      } else {
        setRegCode("");
        setRegDays("");
        await loadRegCodes();
      }
    } catch (e) {
      setRegAddError(e instanceof Error ? e.message : "Error creating code");
    } finally {
      setRegAdding(false);
    }
  }

  async function deleteRegCode() {
    if (!actor || !regDeleteTarget) return;
    setRegDeleting(true);
    try {
      await actor.deleteFreeRegistrationCode(regDeleteTarget.code);
      setRegDeleteTarget(null);
      await loadRegCodes();
    } catch (e) {
      setRegError(e instanceof Error ? e.message : "Failed to delete");
    } finally {
      setRegDeleting(false);
    }
  }

  async function addSubCode() {
    if (!actor || !subCode.trim() || !subValidDays.trim() || !subDurationDays.trim()) return;
    const valid = parseInt(subValidDays, 10);
    const duration = parseInt(subDurationDays, 10);
    if (isNaN(valid) || valid <= 0) { setSubAddError("Valid days must be a positive number"); return; }
    if (isNaN(duration) || duration <= 0) { setSubAddError("Duration days must be a positive number"); return; }
    setSubAdding(true);
    setSubAddError("");
    try {
      const res = await actor.createFreeSubscriptionCode({
        code: subCode.trim().toUpperCase(),
        validDays: BigInt(valid),
        durationDays: BigInt(duration),
      });
      if ("err" in res) {
        setSubAddError(indexErrorMessage(res.err));
      } else {
        setSubCode("");
        setSubValidDays("");
        setSubDurationDays("");
        await loadSubCodes();
      }
    } catch (e) {
      setSubAddError(e instanceof Error ? e.message : "Error creating code");
    } finally {
      setSubAdding(false);
    }
  }

  async function deleteSubCode() {
    if (!actor || !subDeleteTarget) return;
    setSubDeleting(true);
    try {
      await actor.deleteFreeSubscriptionCode(subDeleteTarget.code);
      setSubDeleteTarget(null);
      await loadSubCodes();
    } catch (e) {
      setSubError(e instanceof Error ? e.message : "Failed to delete");
    } finally {
      setSubDeleting(false);
    }
  }

  const formCard: React.CSSProperties = { marginBottom: 24, maxWidth: 520 };
  const fieldRow: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 10 };
  const badge = (redeemed: boolean): React.CSSProperties => ({
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 600,
    background: redeemed ? "var(--color-red-dim, #3a1a1a)" : "var(--color-green-dim, #0a2a1a)",
    color: redeemed ? "var(--color-red, #e05c5c)" : "var(--color-green, #00e87a)",
  });

  return (
    <div>
      <h1 style={{ fontSize: 24, marginBottom: 32 }}>Free Codes</h1>

      {/* ── Registration Codes ── */}
      <h2 style={{ fontSize: 17, marginBottom: 16 }}>Registration Codes</h2>

      {regError && <ErrorBanner message={regError} onDismiss={() => setRegError("")} />}

      {isInstaller && (
        <div className="card" style={formCard}>
          <h3 style={{ marginBottom: 16, fontSize: 15 }}>Create Registration Code</h3>
          <div style={fieldRow}>
            <input
              placeholder="Code (e.g. BETA2026)"
              value={regCode}
              onChange={(e) => setRegCode(e.target.value)}
              style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, textTransform: "uppercase" }}
            />
            <input
              type="number"
              placeholder="Valid for (days)"
              value={regDays}
              min={1}
              onChange={(e) => setRegDays(e.target.value)}
            />
            {regAddError && <p style={{ color: "var(--color-red)", fontSize: 13 }}>{regAddError}</p>}
            <button
              className="btn-primary"
              onClick={addRegCode}
              disabled={regAdding || !regCode.trim() || !regDays.trim()}
              style={{ alignSelf: "flex-start" }}
            >
              {regAdding ? "Creating…" : "Create Code"}
            </button>
          </div>
        </div>
      )}

      {regLoading ? (
        <p style={{ color: "var(--color-muted)" }}>Loading…</p>
      ) : regCodes.length === 0 ? (
        <p style={{ color: "var(--color-muted)", marginBottom: 40 }}>No registration codes yet.</p>
      ) : (
        <div className="card" style={{ padding: 0, maxWidth: 720, marginBottom: 40 }}>
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Created</th>
                <th>Expires</th>
                <th>Redeemed</th>
                {isInstaller && <th></th>}
              </tr>
            </thead>
            <tbody>
              {regCodes.map((c) => (
                <tr key={c.code}>
                  <td style={{ fontFamily: "'DM Mono', monospace", fontWeight: 600 }}>{c.code}</td>
                  <td style={{ fontSize: 13 }}>{formatDate(c.created)}</td>
                  <td style={{ fontSize: 13 }}>{formatDate(expiresAt(c))}</td>
                  <td><span style={badge(c.redeemed)}>{c.redeemed ? "Yes" : "No"}</span></td>
                  {isInstaller && (
                    <td>
                      <button
                        className="btn-danger"
                        style={{ fontSize: 12, height: 30, minHeight: 30, padding: "0 12px" }}
                        onClick={() => setRegDeleteTarget(c)}
                        disabled={c.redeemed}
                        title={c.redeemed ? "Cannot delete a redeemed code" : undefined}
                      >
                        Delete
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Subscription Codes ── */}
      <h2 style={{ fontSize: 17, marginBottom: 16 }}>Subscription Codes</h2>

      {subError && <ErrorBanner message={subError} onDismiss={() => setSubError("")} />}

      {isInstaller && (
        <div className="card" style={formCard}>
          <h3 style={{ marginBottom: 16, fontSize: 15 }}>Create Subscription Code</h3>
          <div style={fieldRow}>
            <input
              placeholder="Code (e.g. TRIAL30)"
              value={subCode}
              onChange={(e) => setSubCode(e.target.value)}
              style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, textTransform: "uppercase" }}
            />
            <input
              type="number"
              placeholder="Valid for (days)"
              value={subValidDays}
              min={1}
              onChange={(e) => setSubValidDays(e.target.value)}
            />
            <input
              type="number"
              placeholder="Subscription duration (days)"
              value={subDurationDays}
              min={1}
              onChange={(e) => setSubDurationDays(e.target.value)}
            />
            {subAddError && <p style={{ color: "var(--color-red)", fontSize: 13 }}>{subAddError}</p>}
            <button
              className="btn-primary"
              onClick={addSubCode}
              disabled={subAdding || !subCode.trim() || !subValidDays.trim() || !subDurationDays.trim()}
              style={{ alignSelf: "flex-start" }}
            >
              {subAdding ? "Creating…" : "Create Code"}
            </button>
          </div>
        </div>
      )}

      {subLoading ? (
        <p style={{ color: "var(--color-muted)" }}>Loading…</p>
      ) : subCodes.length === 0 ? (
        <p style={{ color: "var(--color-muted)" }}>No subscription codes yet.</p>
      ) : (
        <div className="card" style={{ padding: 0, maxWidth: 800 }}>
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Created</th>
                <th>Expires</th>
                <th>Duration</th>
                <th>Redeemed</th>
                {isInstaller && <th></th>}
              </tr>
            </thead>
            <tbody>
              {subCodes.map((c) => (
                <tr key={c.code}>
                  <td style={{ fontFamily: "'DM Mono', monospace", fontWeight: 600 }}>{c.code}</td>
                  <td style={{ fontSize: 13 }}>{formatDate(c.created)}</td>
                  <td style={{ fontSize: 13 }}>{formatDate(expiresAt(c))}</td>
                  <td style={{ fontSize: 13 }}>{Number(c.durationDays)} days</td>
                  <td><span style={badge(c.redeemed)}>{c.redeemed ? "Yes" : "No"}</span></td>
                  {isInstaller && (
                    <td>
                      <button
                        className="btn-danger"
                        style={{ fontSize: 12, height: 30, minHeight: 30, padding: "0 12px" }}
                        onClick={() => setSubDeleteTarget(c)}
                        disabled={c.redeemed}
                        title={c.redeemed ? "Cannot delete a redeemed code" : undefined}
                      >
                        Delete
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={regDeleteTarget !== null}
        title="Delete Registration Code"
        message={regDeleteTarget ? `Delete code "${regDeleteTarget.code}"? This cannot be undone.` : ""}
        confirmLabel={regDeleting ? "Deleting…" : "Delete"}
        onConfirm={deleteRegCode}
        onCancel={() => setRegDeleteTarget(null)}
        danger
      />

      <ConfirmDialog
        open={subDeleteTarget !== null}
        title="Delete Subscription Code"
        message={subDeleteTarget ? `Delete code "${subDeleteTarget.code}"? This cannot be undone.` : ""}
        confirmLabel={subDeleting ? "Deleting…" : "Delete"}
        onConfirm={deleteSubCode}
        onCancel={() => setSubDeleteTarget(null)}
        danger
      />
    </div>
  );
}
